import { useMutation } from '@apollo/client'
import { FunctionComponent, useState } from 'react'
import { Button, NotificationType, Tag } from '../../../../components'
import { useNotification } from '../../../../utils/hooks'
import { Creator, CreatorInterests, User } from '../../../../gql/types'
import { UPDATE_CREATOR } from '../../../../gql'
import { capitalize } from '../../../../utils/helpers'

interface IProps {
  creator: Creator
}

export type EditInterestsFormData = {
  interests: CreatorInterests[]
}

const EditInterests: FunctionComponent<IProps> = ({ creator }) => {
  const [updateCreator, { loading }] = useMutation(UPDATE_CREATOR)
  const [error, setError] = useState(null)
  const { addNotification } = useNotification()

  const [selectedInterests, setSelectedInterests] = useState(
    creator?.interests ?? [],
  )

  const isInterestSelected = (category) => {
    return selectedInterests?.find((value) => value === category) ? true : false
  }

  const selectInterest = (newTopic) => {
    let newSelectedTopics = [...selectedInterests]
    if (isInterestSelected(newTopic)) {
      onSetError(selectedInterests.length - 1)
      newSelectedTopics.splice(newSelectedTopics.indexOf(newTopic), 1)
      return setSelectedInterests(newSelectedTopics)
    }

    onSetError(selectedInterests.length + 1)
    return setSelectedInterests([...selectedInterests, newTopic])
  }

  const onSetError = async (newSelectedTopicsLength) => {
    if (newSelectedTopicsLength === 0) {
      setError('Please select the interests.')
      return
    }

    setError(null)
  }

  const onSubmit = async () => {
    if (!selectedInterests || selectedInterests.length === 0) {
      setError('Please select the interests.')
      return
    }

    try {
      await updateCreator({
        variables: {
          id: creator.id,
          input: {
            interests: selectedInterests,
          },
        },
      })

      addNotification(
        'Interests preferences have been updated.',
        NotificationType.success,
      )
    } catch (error) {
      console.log('@error SetInterests', error.message)
      addNotification(
        `Something went wrong. Please try again.`,
        NotificationType.error,
      )
    }
  }

  return (
    <div className='grid grid-cols-1 gap-x-8 gap-y-8 pt-10 lg:grid-cols-3'>
      <div className='px-4 sm:px-0'>
        <h2 className='text-lg leading-6 font-medium text-gray-900'>
          Interests
        </h2>
        <p className='mt-1 text-sm text-gray-500'>
          Tell us more about what you like to get personalised recommendations.
        </p>
      </div>

      <form
        // onSubmit={onSubmit}
        className='bg-white shadow ring-1 ring-gray-900/5 sm:rounded-xl lg:col-span-2'
      >
        <div className='py-5 md:bg-white space-y-6 sm:p-6'>
          <div className='space-y-4'>
            {Object.keys(CreatorInterests).map((key) => {
              return (
                <Tag
                  key={key}
                  value={key}
                  title={capitalize(key)}
                  isSelected={isInterestSelected(key)}
                  selectTag={(value) => {
                    selectInterest(value)
                  }}
                  className='mr-2 cursor-pointer'
                />
              )
            })}
          </div>
          {error && <p className='mt-2 text-sm text-red-600'>{error}</p>}
        </div>
        <div className='flex items-center justify-end gap-x-6 border-t border-gray-900/10 px-4 py-4 sm:px-8'>
          <Button
            title='Update'
            type='submit'
            loading={loading}
            disabled={loading || error}
            onClick={onSubmit}
          />
        </div>
      </form>
    </div>
  )
}

export { EditInterests }
